import { Dialog, Box, Typography, Button, Slide } from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { forwardRef, useState, useMemo } from "react";
import comingSoonGif from "../../assets/coming-soon-ezgif.com-gif-maker.gif";
import {
  HOME_GLASS_BACKDROP,
  HOME_GLASS_BORDER,
} from "../../pages/Home/constants/homeGlass";

const SlideDown = forwardRef(function SlideDown(props, ref) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const FRIENDLY_ERROR_MESSAGE = "Oops! Something went wrong. Please try again later.";

const getFriendlyAlertMessage = (message) =>
  /networkerror|network error|failed to fetch/i.test(String(message || ""))
    ? FRIENDLY_ERROR_MESSAGE
    : message;

export default function AppAlert({
  open,
  onClose,
  title,
  message,
  isComingSoon = false,
  onConfirm,
  onCancel,
  confirmText = "Confirm",
  cancelText = "Cancel",
  tone = "default",
  backdropSx,
  icon = null,
}) {
  const [gifLoaded, setGifLoaded] = useState(true);
  const isBlueTone = tone === "blue";
  const showSuccessIcon = icon === "success";
  const isErrorLike =
    icon === "error" ||
    /error|failed|wrong|network/i.test(`${title || ""} ${message || ""}`);
  const displayMessage = isErrorLike
    ? getFriendlyAlertMessage(message)
    : message;

  const isConfirmMode = useMemo(
    () => typeof onConfirm === "function" && typeof onCancel === "function",
    [onConfirm, onCancel]
  );

  const closeAlert = () => onClose?.();

  const handleConfirm = () => {
    onConfirm?.();
    closeAlert();
  };

  const handleCancel = () => {
    onCancel?.();
    closeAlert();
  };

  return (
    <Dialog
      open={open}
      onClose={closeAlert}
      TransitionComponent={SlideDown}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      BackdropProps={{
        sx: {
          background:
            "radial-gradient(circle at 45% 35%, rgba(219, 165, 41, 0.18), transparent 34%), radial-gradient(circle at 60% 58%, rgba(47, 64, 34, 0.28), rgba(3, 5, 3, 0.9) 70%)",
          backdropFilter: HOME_GLASS_BACKDROP,
          WebkitBackdropFilter: HOME_GLASS_BACKDROP,
          ...backdropSx,
        },
      }}
      PaperProps={{
        sx: {
          "@keyframes alertSuccessRing": {
            "0%": { opacity: 0, transform: "scale(0.7)" },
            "65%": { opacity: 1, transform: "scale(1.08)" },
            "100%": { opacity: 1, transform: "scale(1)" },
          },
          "@keyframes alertSuccessCheck": {
            "0%": { opacity: 0, transform: "scale(0.55) rotate(-12deg)" },
            "70%": { opacity: 1, transform: "scale(1.14) rotate(0deg)" },
            "100%": { opacity: 1, transform: "scale(1) rotate(0deg)" },
          },
          "@keyframes alertSignalPulse": {
            "0%, 100%": { opacity: 0.45, transform: "scale(1)" },
            "50%": { opacity: 0.9, transform: "scale(1.08)" },
          },
          width: 400,
          maxWidth: "90vw",
          position: "relative",
          background: isErrorLike
            ? "radial-gradient(circle at 50% 0%, rgba(219,165,41,0.2), transparent 44%), linear-gradient(145deg, rgba(44, 47, 37, 0.95), rgba(13, 18, 13, 0.98))"
            : isBlueTone
            ? "linear-gradient(160deg, rgba(17,33,76,0.9), rgba(23,46,115,0.86))"
            : "linear-gradient(145deg, rgba(52, 55, 44, 0.96), rgba(16, 19, 14, 0.98)), radial-gradient(circle at 50% 0%, rgba(219, 165, 41, 0.18), transparent 48%)",
          border: isBlueTone
            ? "1px solid rgba(110,175,255,0.35)"
            : HOME_GLASS_BORDER,
          borderRadius: "24px",
          backdropFilter: HOME_GLASS_BACKDROP,
          WebkitBackdropFilter: HOME_GLASS_BACKDROP,
          boxShadow:
            "inset 0 1px 0 rgba(255,255,255,0.2), 0 24px 70px rgba(0,0,0,0.42)",
          color: "white",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.12), transparent 42%)",
            opacity: 0.75,
          },
        },
      }}
    >
      <Box sx={{ position: "relative", zIndex: 1, p: 4, textAlign: "center" }}>
        {isComingSoon && gifLoaded && (
          <Box
            component="img"
            src={comingSoonGif}
            alt="Coming Soon"
            onError={() => setGifLoaded(false)}
            sx={{
              width: 100,
              height: 100,
              margin: "0 auto 20px",
              borderRadius: "50%",
            }}
          />
        )}

        {isComingSoon && !gifLoaded && (
          <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>
            Coming Soon
          </Typography>
        )}

        {(showSuccessIcon || isErrorLike) && (
          <Box
            aria-hidden
            sx={{
              width: 68,
              height: 68,
              mx: "auto",
              mb: 2,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              border: isErrorLike
                ? "1px solid rgba(219, 165, 41, 0.34)"
                : "3px solid rgba(219, 165, 41, 0.72)",
              background:
                "linear-gradient(145deg, rgba(240, 197, 51, 0.2), rgba(255, 255, 255, 0.04))",
              boxShadow:
                "0 0 26px rgba(219, 165, 41, 0.24), 0 0 38px rgba(255, 255, 255, 0.06)",
              animation: isErrorLike
                ? "alertSignalPulse 1.8s ease-in-out infinite"
                : "alertSuccessRing 480ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {isErrorLike ? (
              <ErrorOutlineRoundedIcon sx={{ fontSize: 34, color: "#f3c94a" }} />
            ) : (
              <CheckRoundedIcon
                sx={{
                  fontSize: 36,
                  color: "#f3c94a",
                  animation:
                    "alertSuccessCheck 520ms 120ms cubic-bezier(0.22, 1, 0.36, 1) both",
                }}
              />
            )}
          </Box>
        )}

        {isErrorLike ? (
          <Typography
            sx={{
              fontSize: 20,
              fontWeight: 600,
              mb: 4,
              lineHeight: 1.5,
              color: "#e2e8f0",
            }}
          >
            {displayMessage || "Something went wrong. Please try again."}
          </Typography>
        ) : (
          <>
        <Typography sx={{ fontSize: 24, fontWeight: 600, mb: 1.5 }}>
          {title}
        </Typography>

        <Typography
          sx={{
            fontSize: 14,
            color: "#cbd5e1",
            mb: 4,
            lineHeight: 1.6,
          }}
        >
          {displayMessage}
        </Typography>
          </>
        )}

        {isConfirmMode ? (
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button
              onClick={handleCancel}
              sx={{
                px: 3,
                py: 1,
                background: "transparent",
                color: "#cbd5e1",
                border: "1px solid #cbd5e1",
                borderRadius: 2,
                fontWeight: 600,
                "&:hover": {
                  background: "rgba(203, 213, 225, 0.1)",
                },
              }}
            >
              {cancelText}
            </Button>

            <Button
              onClick={handleConfirm}
              sx={{
                px: 3,
                py: 1,
                background: isBlueTone
                  ? "linear-gradient(135deg, #3b82f6, #2563eb)"
                  : showSuccessIcon
                    ? "linear-gradient(135deg, #dba529, #b48213)"
                    : "linear-gradient(135deg, #dba529, #8f6c12)",
                color: "white",
                borderRadius: 2,
                fontWeight: 600,
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: isBlueTone
                    ? "0 8px 20px rgba(37, 99, 235, 0.55)"
                    : showSuccessIcon
                      ? "0 8px 20px rgba(219, 165, 41, 0.42)"
                      : "0 8px 20px rgba(219, 165, 41, 0.36)",
                },
              }}
            >
              {confirmText}
            </Button>
          </Box>
        ) : (
          <Button
            onClick={closeAlert}
            sx={{
              px: 4,
              py: 1,
              background: showSuccessIcon
                ? "linear-gradient(135deg, #dba529, #b48213)"
                : "linear-gradient(135deg, #dba529, #8f6c12)",
              color: "white",
              borderRadius: 2,
              fontWeight: 600,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: showSuccessIcon
                  ? "0 8px 20px rgba(219, 165, 41, 0.42)"
                  : "0 8px 20px rgba(219, 165, 41, 0.36)",
              },
            }}
          >
            Got it
          </Button>
        )}
      </Box>
    </Dialog>
  );
}